import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { CalendarRange, ChevronDown, ChevronUp, Search, SlidersHorizontal, X } from 'lucide-react';
import { DateInput } from '@/components/DateInput';

export interface FilterOption {
  value: string;
  label: string;
}

/**
 * FilterBar — busca livre + filtros rápidos, sempre acima da tabela.
 *
 * A busca é "debounced": o operador digita sem disparar uma requisição por
 * tecla, mas não precisa apertar Enter.
 */
export function FilterBar({
  placeholder,
  search,
  onSearchChange,
  filters,
  from,
  to,
  onFromChange,
  onToChange,
  moreFilters,
  activeMoreFilters = 0,
  onClearMore,
  children,
  debounceMs = 300,
}: {
  placeholder: string;
  search: string;
  onSearchChange: (value: string) => void;
  filters?: Array<{
    key: string;
    label: string;
    value: string;
    options: FilterOption[];
    onChange: (value: string) => void;
  }>;
  /**
   * Período, na linha principal.
   *
   * Fica à vista e não em "Mais filtros" porque quase toda lista do sistema é
   * lida por recorte de mês — esconder isso faz o operador achar que a tela
   * mostra tudo o que existe.
   */
  from?: string;
  to?: string;
  onFromChange?: (value: string) => void;
  onToChange?: (value: string) => void;
  /**
   * Filtros de segunda ordem, escondidos até alguém pedir.
   *
   * Doze seletores lado a lado deixam a tela intimidadora e ninguém usa
   * nenhum. Os quatro do dia a dia ficam à vista; o resto abre no botão.
   */
  moreFilters?: ReactNode;
  /** Quantos dos escondidos estão preenchidos — vira o número no botão. */
  activeMoreFilters?: number;
  onClearMore?: () => void;
  children?: ReactNode;
  debounceMs?: number;
}) {
  const [draft, setDraft] = useState(search);
  const [syncedWith, setSyncedWith] = useState(search);
  // Abre sozinho quando já há filtro escondido em uso: senão o operador vê a
  // lista curta e não descobre por quê.
  const [aberto, setAberto] = useState(activeMoreFilters > 0);

  // Mantém o campo em sincronia quando o filtro é limpo de fora (botão Limpar,
  // navegação). Ajuste durante o render: sem efeito, sem render extra.
  if (search !== syncedWith) {
    setSyncedWith(search);
    setDraft(search);
  }

  useEffect(() => {
    if (draft === search) return;
    const timer = setTimeout(() => onSearchChange(draft), debounceMs);
    return () => clearTimeout(timer);
  }, [draft, search, onSearchChange, debounceMs]);

  return (
    <div className="filterbar">
      <div className="filterbar__search">
        <Search size={15} aria-hidden />
        <input
          className="input"
          type="search"
          placeholder={placeholder}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-label={placeholder}
        />
      </div>

      {filters?.map((filter) => (
        <label key={filter.key} className="sr-only-wrap">
          <span className="sr-only">{filter.label}</span>
          <select
            className="filterbar__select"
            value={filter.value}
            onChange={(event) => filter.onChange(event.target.value)}
          >
            <option value="">{filter.label}: todos</option>
            {filter.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ))}

      {onFromChange && onToChange && (
        <div className="filterbar__range">
          <CalendarRange size={13} aria-hidden />
          <DateInput
            className=""
            aria-label="Data inicial"
            value={from ?? ''}
            max={to || undefined}
            onChange={(iso) => onFromChange(iso)}
          />
          <span aria-hidden>→</span>
          <DateInput
            className=""
            aria-label="Data final"
            value={to ?? ''}
            min={from || undefined}
            onChange={(iso) => onToChange(iso)}
          />
        </div>
      )}

      {moreFilters && (
        <button
          type="button"
          className={`filterbar__chip${activeMoreFilters > 0 ? ' is-active' : ''}`}
          onClick={() => setAberto((atual) => !atual)}
          aria-expanded={aberto}
        >
          <SlidersHorizontal size={12} aria-hidden />
          Mais filtros
          {activeMoreFilters > 0 && ` (${activeMoreFilters})`}
          {aberto ? <ChevronUp size={12} aria-hidden /> : <ChevronDown size={12} aria-hidden />}
        </button>
      )}

      {(search || filters?.some((f) => f.value) || activeMoreFilters > 0 || from || to) && (
        <button
          type="button"
          className="filterbar__chip"
          onClick={() => {
            setDraft('');
            onSearchChange('');
            filters?.forEach((filter) => filter.onChange(''));
            onFromChange?.('');
            onToChange?.('');
            onClearMore?.();
          }}
        >
          <X size={12} aria-hidden /> Limpar
        </button>
      )}

      <div className="filterbar__spacer" />
      {children}

      {moreFilters && aberto && <div className="filterbar__more">{moreFilters}</div>}
    </div>
  );
}
